import { LanguagesEnum } from '@/api/enums';
import { PhishingSign, PhishingSignTranslation } from '@/client/campaigns';
import {
  useDeletePhishingSign,
  usePhishingSigns,
  useUpdatePhishingSign,
} from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const normalizeQuillValue = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '<p><br></p>') {
    return null;
  }
  return trimmed;
};

const updateTranslation = (
  sign: PhishingSign,
  language: LanguagesEnum,
  value: string,
): PhishingSign => {
  const content = normalizeQuillValue(value);
  const existingIndex = sign.translations.findIndex(
    (translation) => translation.language === language,
  );

  let translations: PhishingSignTranslation[];
  if (existingIndex >= 0) {
    translations = sign.translations.map((translation, index) =>
      index === existingIndex ? { ...translation, content } : translation,
    );
  } else {
    translations = [...sign.translations, { language, content }];
  }

  return { ...sign, translations };
};

export const usePhishingSignsEditor = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { phishingSigns, isLoading } = usePhishingSigns();
  const { update, isLoading: isUpdating } = useUpdatePhishingSign();
  const { delete: deleteSign, isLoading: isDeleting } = useDeletePhishingSign();

  const [signs, setSigns] = useState<PhishingSign[]>([]);
  const [signsCached, setSignsCached] = useState<PhishingSign[]>([]);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    const result = phishingSigns?.result ?? [];
    setSigns(result);
    setSignsCached(result);
  }, [phishingSigns?.result]);

  useEffect(() => {
    setIsChanged(!isEqual(signs, signsCached));
  }, [signs, signsCached]);

  const isSaving = isUpdating || isDeleting;

  const handleEnChange = (signId: string, value: string): void => {
    setSigns((current) =>
      current.map((sign) =>
        sign.id === signId
          ? updateTranslation(sign, LanguagesEnum.EN, value)
          : sign,
      ),
    );
  };

  const handleDaChange = (signId: string, value: string): void => {
    setSigns((current) =>
      current.map((sign) =>
        sign.id === signId
          ? updateTranslation(sign, LanguagesEnum.DK, value)
          : sign,
      ),
    );
  };

  const handleDelete = (signId: string): void => {
    setSigns((current) => current.filter((sign) => sign.id !== signId));
  };

  const handleCancel = (): void => {
    setSigns(signsCached);
  };

  const handleSave = async (): Promise<void> => {
    const cachedById = new Map(signsCached.map((sign) => [sign.id, sign]));
    const currentIds = new Set(signs.map((sign) => sign.id));

    const toUpdate = signs.filter((current) => {
      const cached = cachedById.get(current.id);
      return cached && !isEqual(current.translations, cached.translations);
    });
    const toDelete = signsCached.filter((cached) => !currentIds.has(cached.id));

    const operations = [
      ...toUpdate.map(async (sign) =>
        update({ id: sign.id, translations: sign.translations }),
      ),
      ...toDelete.map(async (sign) => deleteSign(sign.id)),
    ];

    const results = await Promise.allSettled(operations);
    const failures = results.filter((r) => r.status === 'rejected').length;

    if (failures === 0) {
      toast?.success(t('toast.success'), t('phishingSigns.saved'));
    } else {
      toast?.error(
        t('toast.error'),
        t('phishingSigns.saveFailedCount', { count: failures }),
      );
    }
  };

  return {
    signs,
    isLoading,
    isChanged,
    isSaving,
    handleSave,
    handleCancel,
    handleEnChange,
    handleDaChange,
    handleDelete,
  };
};
